/**
 * KnowledgeCheckPanel — Story 4-3a: Knowledge check authoring panel.
 * Story 4-3c: Extends with scenario flavor affordance (narrativeContext + behaviorCue fields).
 *
 * Handles opt-in toggle, 1–3 MCQ question editor, AI draft request,
 * approval gate (clinical-lead only), scope-fence violation display,
 * and edit re-gate banner.
 *
 * Scenario flavor: each question can be toggled to 'scenario' type, which
 * surfaces narrativeContext (≤500 chars) and behaviorCue (≤200 chars) fields.
 * The scope fence runs over narrativeContext at approval time (server-side);
 * inline validation shows char-cap warnings before submit.
 *
 * @NonPhiEndpoint — knowledge-check content is clinical corpus, not member PHI.
 */

import type { JSX } from 'react';
import { useState } from 'react';

// ── Constants ─────────────────────────────────────────────────────────────────

// MUST stay in sync with the domain source of truth:
// services/betty-engagement/src/domain/education-content/knowledge-check.entity.ts
// (NARRATIVE_CONTEXT_MAX_CHARS, BEHAVIOR_CUE_MAX_CHARS, QuestionFlavor). No shared
// contracts package exists yet to import from; the server fence is authoritative.
/** Cohort-appropriate readability cap (≈3 short sentences). */
export const NARRATIVE_CONTEXT_MAX_CHARS = 500;
export const BEHAVIOR_CUE_MAX_CHARS = 200;

// ── Types ──────────────────────────────────────────────────────────────────────

export type QuestionFlavor = 'plain' | 'scenario';

export interface KnowledgeCheckOptionData {
  optionId: string;
  text: string;
}

/** Illustrative image data (Story 4.3d). Human-sourced only — no AI-generated images. */
export interface IllustrativeImageData {
  assetId: string;
  altText: string; // required when image is present
  uploadedBy: string;
  uploadedAt: string;
  culturalReviewStatus: 'pending' | 'approved' | 'rejected';
  perTrackTags: string[];
}

export interface KnowledgeCheckQuestionData {
  questionId: string;
  stem: string;
  options: KnowledgeCheckOptionData[];
  correctOptionId: string;
  reinforcementCopy: string;
  // Story 4.3d image-attachment fields
  illustrativeImageUrl?: string; // URL preview for the uploaded image
  illustrativeImageAltText?: string; // required before save when image attached
  perTrackTags?: string; // comma-separated track tags
  /**
   * Cultural-appropriateness review status (AC2 — operable reviewer workflow).
   * Required to be 'approved' before an image-bearing question can be published.
   */
  culturalReviewStatus?: 'pending' | 'approved' | 'rejected';
  answerableFromTextAlone?: boolean; // RD-affirmed gate (required before approval)
  answerableFromTextAffirmedBy?: string;
  answerableFromTextAffirmedAt?: string;
  /** Additive flavor field — default 'plain'. */
  questionFlavor?: QuestionFlavor;
  /** Required when questionFlavor='scenario'. Max 500 chars. */
  narrativeContext?: string;
  /** Required when questionFlavor='scenario'. Max 200 chars. */
  behaviorCue?: string;
}

export interface KnowledgeCheckData {
  checkId: string;
  status: 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'PUBLISHED' | 'ARCHIVED';
  questions: KnowledgeCheckQuestionData[];
  language: 'en' | 'es';
  origin: 'human-authored' | 'ai-drafted' | 'ai-edited';
  approvedBy: string | null;
  approvedAt: string | null;
}

export interface KnowledgeCheckFormData {
  questions: KnowledgeCheckQuestionData[];
  language: 'en' | 'es';
}

export interface AiCheckDraftData {
  origin: 'ai-drafted';
  language: 'en' | 'es';
  questions: KnowledgeCheckQuestionData[];
  checkWorthySuggestion: boolean;
}

export interface ScopeFenceViolationData {
  category:
    | 'diagnosis'
    | 'medication'
    | 'symptom-triage'
    | 'behavioral-health'
    | 'emotional-eating-mood';
  field: 'stem' | 'distractor' | 'reinforcementCopy' | 'narrativeContext';
  questionId: string;
  text: string;
}

export interface KnowledgeCheckPanelProps {
  lessonId: string;
  initialCheckAttached: boolean;
  initialCheck?: KnowledgeCheckData | null;
  /** true for staff:clinical-lead */
  isApprover: boolean;
  onOptInToggle: (checkAttached: boolean) => void;
  onSaveCheck: (data: KnowledgeCheckFormData) => void;
  onApproveCheck: (checkId: string) => void;
  onRequestAiDraft: () => void;
  aiDraft?: AiCheckDraftData | null;
  scopeFenceViolations?: ScopeFenceViolationData[];
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<KnowledgeCheckData['status'], string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending Approval',
  APPROVED: 'Approved',
  PUBLISHED: 'Published',
  ARCHIVED: 'Archived',
};

const STATUS_BADGE_CLASSES: Record<KnowledgeCheckData['status'], string> = {
  DRAFT: 'bg-yellow-100 text-yellow-800',
  PENDING_APPROVAL: 'bg-blue-100 text-blue-800',
  APPROVED: 'bg-green-100 text-green-800',
  PUBLISHED: 'bg-teal-100 text-teal-800',
  ARCHIVED: 'bg-gray-100 text-gray-700',
};

function makeEmptyQuestion(index: number): KnowledgeCheckQuestionData {
  return {
    questionId: `q-new-${Date.now()}-${index}`,
    stem: '',
    options: [
      { optionId: 'opt-a', text: '' },
      { optionId: 'opt-b', text: '' },
    ],
    correctOptionId: 'opt-a',
    reinforcementCopy: '',
    // exactOptionalPropertyTypes: omit optional keys rather than setting them to undefined.
    // Default flavor is plain (no scenario fields).
  };
}

/** True when the question has scenario-specific validation errors. */
function getScenarioValidationErrors(q: KnowledgeCheckQuestionData): string[] {
  const errors: string[] = [];
  if ((q.questionFlavor ?? 'plain') !== 'scenario') return errors;

  if (!q.narrativeContext || q.narrativeContext.trim() === '') {
    errors.push('Narrative context is required for scenario questions.');
  } else if (q.narrativeContext.length > NARRATIVE_CONTEXT_MAX_CHARS) {
    errors.push(
      `Narrative context exceeds ${NARRATIVE_CONTEXT_MAX_CHARS} character readability cap (${q.narrativeContext.length} chars).`,
    );
  }
  if (!q.behaviorCue || q.behaviorCue.trim() === '') {
    errors.push('Behaviour cue is required for scenario questions.');
  } else if (q.behaviorCue.length > BEHAVIOR_CUE_MAX_CHARS) {
    errors.push(
      `Behaviour cue exceeds ${BEHAVIOR_CUE_MAX_CHARS} character cap (${q.behaviorCue.length} chars).`,
    );
  }
  return errors;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function KnowledgeCheckPanel({
  lessonId: _lessonId,
  initialCheckAttached,
  initialCheck,
  isApprover,
  onOptInToggle,
  onSaveCheck,
  onApproveCheck,
  onRequestAiDraft,
  aiDraft: _aiDraft,
  scopeFenceViolations = [],
}: KnowledgeCheckPanelProps): JSX.Element {
  const [checkAttached, setCheckAttached] = useState(initialCheckAttached);
  const [questions, setQuestions] = useState<KnowledgeCheckQuestionData[]>(
    initialCheck?.questions.length ? initialCheck.questions : [makeEmptyQuestion(0)],
  );
  const [language, setLanguage] = useState<'en' | 'es'>(initialCheck?.language ?? 'en');

  const currentStatus = initialCheck?.status ?? null;
  const isEditingApprovedOrPublished =
    currentStatus === 'APPROVED' || currentStatus === 'PUBLISHED';

  const handleToggle = (): void => {
    const next = !checkAttached;
    setCheckAttached(next);
    onOptInToggle(next);
  };

  const handleStemChange = (qIndex: number, value: string): void => {
    setQuestions((prev) => prev.map((q, i) => (i === qIndex ? { ...q, stem: value } : q)));
  };

  const handleOptionTextChange = (qIndex: number, oIndex: number, value: string): void => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qIndex) return q;
        return {
          ...q,
          options: q.options.map((o, oi) => (oi === oIndex ? { ...o, text: value } : o)),
        };
      }),
    );
  };

  const handleCorrectOptionChange = (qIndex: number, optionId: string): void => {
    setQuestions((prev) =>
      prev.map((q, i) => (i === qIndex ? { ...q, correctOptionId: optionId } : q)),
    );
  };

  const handleReinforcementChange = (qIndex: number, value: string): void => {
    setQuestions((prev) =>
      prev.map((q, i) => (i === qIndex ? { ...q, reinforcementCopy: value } : q)),
    );
  };

  // ── Scenario flavor handlers ────────────────────────────────────────────────

  const handleFlavorChange = (qIndex: number, flavor: QuestionFlavor): void => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qIndex) return q;
        // Switching to plain: clear scenario-specific fields
        if (flavor === 'plain') {
          const { narrativeContext: _nc, behaviorCue: _bc, ...rest } = q;
          return { ...rest, questionFlavor: 'plain' };
        }
        return { ...q, questionFlavor: 'scenario' };
      }),
    );
  };

  const handleNarrativeContextChange = (qIndex: number, value: string): void => {
    setQuestions((prev) =>
      prev.map((q, i) => (i === qIndex ? { ...q, narrativeContext: value } : q)),
    );
  };

  const handleBehaviorCueChange = (qIndex: number, value: string): void => {
    setQuestions((prev) => prev.map((q, i) => (i === qIndex ? { ...q, behaviorCue: value } : q)));
  };

  // ── Standard question management ────────────────────────────────────────────

  const handleAddQuestion = (): void => {
    if (questions.length >= 3) return;
    setQuestions((prev) => [...prev, makeEmptyQuestion(prev.length)]);
  };

  const handleRemoveQuestion = (qIndex: number): void => {
    if (questions.length <= 1) return;
    setQuestions((prev) => prev.filter((_, i) => i !== qIndex));
  };

  const handleAddOption = (qIndex: number): void => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qIndex || q.options.length >= 4) return q;
        const oIndex = q.options.length;
        return {
          ...q,
          options: [...q.options, { optionId: `opt-new-${Date.now()}-${oIndex}`, text: '' }],
        };
      }),
    );
  };

  const handleRemoveOption = (qIndex: number, oIndex: number): void => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qIndex || q.options.length <= 2) return q;
        const removedId = q.options[oIndex]?.optionId;
        const remaining = q.options.filter((_, oi) => oi !== oIndex);
        return {
          ...q,
          options: remaining,
          correctOptionId:
            q.correctOptionId === removedId
              ? (remaining[0]?.optionId ?? q.correctOptionId)
              : q.correctOptionId,
        };
      }),
    );
  };

  // Story 4.3d image-attachment handlers
  const handleImageUrlChange = (qIndex: number, value: string): void => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qIndex) return q;
        const { illustrativeImageUrl: _removed, ...rest } = q;
        return value ? { ...rest, illustrativeImageUrl: value } : rest;
      }),
    );
  };

  const handleImageAltTextChange = (qIndex: number, value: string): void => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qIndex) return q;
        const { illustrativeImageAltText: _removed, ...rest } = q;
        return value ? { ...rest, illustrativeImageAltText: value } : rest;
      }),
    );
  };

  const handlePerTrackTagsChange = (qIndex: number, value: string): void => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qIndex) return q;
        const { perTrackTags: _removed, ...rest } = q;
        return value ? { ...rest, perTrackTags: value } : rest;
      }),
    );
  };

  const handleAnswerableFromTextChange = (qIndex: number, value: boolean): void => {
    setQuestions((prev) =>
      prev.map((q, i) => (i === qIndex ? { ...q, answerableFromTextAlone: value } : q)),
    );
  };

  const handleCulturalReviewChange = (
    qIndex: number,
    value: 'pending' | 'approved' | 'rejected',
  ): void => {
    setQuestions((prev) =>
      prev.map((q, i) => (i === qIndex ? { ...q, culturalReviewStatus: value } : q)),
    );
  };

  // Inline validation: compute scenario errors once per question, reused below.
  const scenarioErrorsByIndex = questions.map((q) => getScenarioValidationErrors(q));
  const allScenarioErrors = scenarioErrorsByIndex.flatMap((errors, i) =>
    errors.map((error) => ({ qIndex: i, error })),
  );

  const handleSave = (): void => {
    // Block save when scenario validation errors exist — scenario fields are
    // required for clinical correctness; submitting with invalid data would
    // persist broken checks that fail the scope-fence at approval time anyway.
    if (allScenarioErrors.length > 0) return;
    onSaveCheck({ questions, language });
  };

  const showApproveBtn =
    isApprover &&
    initialCheck != null &&
    initialCheck.checkId !== '' &&
    (currentStatus === 'DRAFT' || currentStatus === 'PENDING_APPROVAL');

  // Story 4.3d: block approval if any image-bearing question fails the accessibility or cultural-review gate
  const hasUnaffirmedImageQuestions = questions.some(
    (q) =>
      q.illustrativeImageUrl &&
      (q.answerableFromTextAlone !== true ||
        !q.illustrativeImageAltText ||
        q.culturalReviewStatus !== 'approved'),
  );
  const canApprove =
    showApproveBtn && scopeFenceViolations.length === 0 && !hasUnaffirmedImageQuestions;

  return (
    <section className="space-y-4 rounded-md border border-gray-200 p-4">
      {/* Section heading */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Knowledge Check</h2>
        {currentStatus && (
          <span
            data-testid="check-status-badge"
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASSES[currentStatus]}`}
          >
            {STATUS_LABELS[currentStatus]}
          </span>
        )}
      </div>

      {/* Lesson edit re-gate note */}
      {initialCheck != null && (
        <p className="text-xs text-amber-700">
          Note: editing this lesson will return its attached knowledge check to DRAFT
        </p>
      )}

      {/* Opt-in toggle */}
      <label className="flex cursor-pointer items-center gap-3 text-sm font-medium text-gray-700">
        <input
          type="checkbox"
          data-testid="check-opt-in-toggle"
          checked={checkAttached}
          onChange={handleToggle}
          className="rounded border-gray-300"
        />
        Attach Knowledge Check
      </label>

      {!checkAttached ? (
        <p className="text-sm text-gray-500">No knowledge check for this lesson</p>
      ) : (
        <div className="space-y-6">
          {/* Edit re-gate banner for approved/published */}
          {isEditingApprovedOrPublished && (
            <div
              data-testid="edit-regate-banner"
              className="rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-800"
            >
              Editing this item returns it to DRAFT for re-approval
            </div>
          )}

          {/* Language selector */}
          <div>
            <label htmlFor="check-language" className="block text-xs font-medium text-gray-600">
              Language
            </label>
            <select
              id="check-language"
              data-testid="check-language-select"
              value={language}
              onChange={(e) => {
                setLanguage(e.target.value as 'en' | 'es');
              }}
              className="mt-1 block rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            >
              <option value="en">English (en)</option>
              <option value="es">Spanish (es) — requires certified translator</option>
            </select>
          </div>

          {/* Questions */}
          {questions.map((q, qIndex) => {
            const isScenario = (q.questionFlavor ?? 'plain') === 'scenario';
            const scenarioErrors = scenarioErrorsByIndex[qIndex] ?? [];

            return (
              <div
                key={q.questionId}
                className="space-y-3 rounded-md border border-gray-100 bg-gray-50 p-4"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Question {qIndex + 1}</span>
                  <button
                    type="button"
                    data-testid={`remove-question-btn-${qIndex}`}
                    onClick={() => handleRemoveQuestion(qIndex)}
                    disabled={questions.length <= 1}
                    className="text-xs text-red-600 hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Remove
                  </button>
                </div>

                {/* Question flavor selector — Story 4.3c */}
                <div>
                  <label
                    htmlFor={`question-flavor-${qIndex}`}
                    className="block text-xs font-medium text-gray-600"
                  >
                    Question type
                  </label>
                  <select
                    id={`question-flavor-${qIndex}`}
                    data-testid={`question-flavor-${qIndex}`}
                    value={q.questionFlavor ?? 'plain'}
                    onChange={(e) => handleFlavorChange(qIndex, e.target.value as QuestionFlavor)}
                    className="mt-1 block rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                  >
                    <option value="plain">Plain MCQ</option>
                    <option value="scenario">Text Scenario (richer-context MCQ, no image)</option>
                  </select>
                </div>

                {/* Scenario fields — only shown when flavor=scenario */}
                {isScenario && (
                  <div
                    data-testid={`scenario-fields-${qIndex}`}
                    className="space-y-3 rounded-md border border-teal-100 bg-teal-50 p-3"
                  >
                    <p className="text-xs font-medium text-teal-800">
                      Scenario fields — ACCESS scope fence applies to narrative context and all
                      distractors. No emotional-eating / mood / diagnosis / medication /
                      lab-interpretation framing.
                    </p>

                    {/* Narrative context */}
                    <div>
                      <label
                        htmlFor={`narrative-context-${qIndex}`}
                        className="block text-xs font-medium text-gray-600"
                      >
                        Narrative context{' '}
                        <span className="text-gray-400">
                          (scenario stem — max {NARRATIVE_CONTEXT_MAX_CHARS} chars)
                        </span>
                      </label>
                      <textarea
                        id={`narrative-context-${qIndex}`}
                        data-testid={`narrative-context-${qIndex}`}
                        value={q.narrativeContext ?? ''}
                        onChange={(e) => handleNarrativeContextChange(qIndex, e.target.value)}
                        rows={3}
                        maxLength={NARRATIVE_CONTEXT_MAX_CHARS}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        placeholder="Describe the scenario (e.g. 'You are at a family dinner…'). Keep it concise — 1–3 sentences."
                      />
                      <p className="mt-0.5 text-right text-xs text-gray-400">
                        {(q.narrativeContext ?? '').length} / {NARRATIVE_CONTEXT_MAX_CHARS}
                      </p>
                    </div>

                    {/* Behavior cue */}
                    <div>
                      <label
                        htmlFor={`behavior-cue-${qIndex}`}
                        className="block text-xs font-medium text-gray-600"
                      >
                        Behaviour cue{' '}
                        <span className="text-gray-400">
                          (OAP-tied takeaway — max {BEHAVIOR_CUE_MAX_CHARS} chars)
                        </span>
                      </label>
                      <input
                        id={`behavior-cue-${qIndex}`}
                        type="text"
                        data-testid={`behavior-cue-${qIndex}`}
                        value={q.behaviorCue ?? ''}
                        onChange={(e) => handleBehaviorCueChange(qIndex, e.target.value)}
                        maxLength={BEHAVIOR_CUE_MAX_CHARS}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        placeholder="Name one concrete OAP-tied behaviour (e.g. 'Choose grilled chicken over pasta to stay within your phosphorus target.')"
                      />
                      <p className="mt-0.5 text-right text-xs text-gray-400">
                        {(q.behaviorCue ?? '').length} / {BEHAVIOR_CUE_MAX_CHARS}
                      </p>
                    </div>

                    {/* Inline scenario validation errors */}
                    {scenarioErrors.length > 0 && (
                      <ul
                        data-testid={`scenario-validation-errors-${qIndex}`}
                        className="space-y-1 text-xs text-red-700"
                      >
                        {scenarioErrors.map((err, i) => (
                          <li key={i}>⚠ {err}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Stem */}
                <div>
                  <label
                    htmlFor={`question-stem-${qIndex}`}
                    className="block text-xs font-medium text-gray-600"
                  >
                    {isScenario ? 'Question stem (asked after narrative)' : 'Question stem'}
                  </label>
                  <input
                    id={`question-stem-${qIndex}`}
                    type="text"
                    data-testid={`question-stem-${qIndex}`}
                    value={q.stem}
                    onChange={(e) => handleStemChange(qIndex, e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    required
                  />
                </div>

                {/* Options */}
                <fieldset>
                  <legend className="text-xs font-medium text-gray-600">
                    Answer options (mark correct)
                  </legend>
                  <div className="mt-2 space-y-2">
                    {q.options.map((opt, oIndex) => (
                      <div key={opt.optionId} className="flex items-center gap-2">
                        <input
                          type="radio"
                          data-testid={`correct-option-radio-${qIndex}-${oIndex}`}
                          name={`correct-option-${q.questionId}`}
                          value={opt.optionId}
                          checked={q.correctOptionId === opt.optionId}
                          onChange={() => handleCorrectOptionChange(qIndex, opt.optionId)}
                          aria-label={`Mark option ${oIndex + 1} as correct`}
                          className="border-gray-300"
                        />
                        <input
                          type="text"
                          value={opt.text}
                          onChange={(e) => handleOptionTextChange(qIndex, oIndex, e.target.value)}
                          placeholder={`Option ${oIndex + 1}`}
                          className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                        />
                        <button
                          type="button"
                          data-testid={`remove-option-btn-${qIndex}-${oIndex}`}
                          onClick={() => handleRemoveOption(qIndex, oIndex)}
                          disabled={q.options.length <= 2}
                          className="text-xs text-red-500 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-30"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    data-testid={`add-option-btn-${qIndex}`}
                    onClick={() => handleAddOption(qIndex)}
                    disabled={q.options.length >= 4}
                    className="mt-2 text-xs font-medium text-teal-600 hover:text-teal-800 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    + Add option
                  </button>
                </fieldset>

                {/* Reinforcement copy */}
                <div>
                  <label
                    htmlFor={`reinforcement-copy-${qIndex}`}
                    className="block text-xs font-medium text-gray-600"
                  >
                    Reinforcement copy
                  </label>
                  <textarea
                    id={`reinforcement-copy-${qIndex}`}
                    value={q.reinforcementCopy}
                    onChange={(e) => handleReinforcementChange(qIndex, e.target.value)}
                    rows={2}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>

                {/* Story 4.3d: Illustrative image attachment */}
                <div className="mt-4 space-y-3 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <p className="text-xs font-semibold text-amber-800">
                    Illustrative image (optional) — human-sourced only; no AI-generated images
                  </p>
                  <p className="text-xs text-amber-700">
                    The image must illustrate only. The question must be fully answerable from
                    text/alt-text alone (cover the image, read the alt text, try to answer).
                  </p>

                  {/* Image URL / asset reference */}
                  <div>
                    <label
                      htmlFor={`image-url-${qIndex}`}
                      className="block text-xs font-medium text-gray-600"
                    >
                      Image URL or asset ID
                    </label>
                    <input
                      id={`image-url-${qIndex}`}
                      type="url"
                      data-testid={`image-url-${qIndex}`}
                      value={q.illustrativeImageUrl ?? ''}
                      onChange={(e) => handleImageUrlChange(qIndex, e.target.value)}
                      placeholder="https://..."
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>

                  {/* Alt text — required when image is present */}
                  <div>
                    <label
                      htmlFor={`image-alt-text-${qIndex}`}
                      className="block text-xs font-medium text-gray-600"
                    >
                      Alt text{q.illustrativeImageUrl ? ' (required)' : ''}
                    </label>
                    <input
                      id={`image-alt-text-${qIndex}`}
                      type="text"
                      data-testid={`image-alt-text-${qIndex}`}
                      value={q.illustrativeImageAltText ?? ''}
                      onChange={(e) => handleImageAltTextChange(qIndex, e.target.value)}
                      placeholder="Describe what the image shows (for screen readers)"
                      className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm ${
                        q.illustrativeImageUrl && !q.illustrativeImageAltText
                          ? 'border-red-400 bg-red-50'
                          : 'border-gray-300'
                      }`}
                      required={!!q.illustrativeImageUrl}
                      aria-required={!!q.illustrativeImageUrl}
                    />
                    {q.illustrativeImageUrl && !q.illustrativeImageAltText && (
                      <p
                        role="alert"
                        data-testid={`image-alt-text-error-${qIndex}`}
                        className="mt-1 text-xs text-red-600"
                      >
                        Alt text is required before this image can be saved.
                      </p>
                    )}
                  </div>

                  {/* Per-condition-track tags */}
                  <div>
                    <label
                      htmlFor={`per-track-tags-${qIndex}`}
                      className="block text-xs font-medium text-gray-600"
                    >
                      Condition-track tags (comma-separated, e.g. CKD, HTN)
                    </label>
                    <input
                      id={`per-track-tags-${qIndex}`}
                      type="text"
                      data-testid={`per-track-tags-${qIndex}`}
                      value={q.perTrackTags ?? ''}
                      onChange={(e) => handlePerTrackTagsChange(qIndex, e.target.value)}
                      placeholder="CKD, HTN, ASCVD"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Required. No &quot;generic healthy choice&quot; tags allowed.
                    </p>
                  </div>

                  {/* Cultural-appropriateness review (AC2 — operable reviewer workflow) */}
                  <div>
                    <label
                      htmlFor={`cultural-review-status-${qIndex}`}
                      className="block text-xs font-medium text-gray-600"
                    >
                      Cultural-appropriateness review
                      {q.illustrativeImageUrl ? ' (required before approval)' : ''}
                    </label>
                    <select
                      id={`cultural-review-status-${qIndex}`}
                      data-testid={`cultural-review-status-${qIndex}`}
                      value={q.culturalReviewStatus ?? 'pending'}
                      onChange={(e) =>
                        handleCulturalReviewChange(
                          qIndex,
                          e.target.value as 'pending' | 'approved' | 'rejected',
                        )
                      }
                      className={`mt-1 block rounded-md border px-3 py-1.5 text-sm ${
                        q.illustrativeImageUrl && q.culturalReviewStatus !== 'approved'
                          ? 'border-amber-400 bg-amber-50'
                          : 'border-gray-300'
                      }`}
                    >
                      <option value="pending">Pending review</option>
                      <option value="approved">Approved — culturally appropriate</option>
                      <option value="rejected">Rejected — requires revision</option>
                    </select>
                    {q.illustrativeImageUrl && q.culturalReviewStatus === 'rejected' && (
                      <p
                        role="alert"
                        data-testid={`cultural-review-rejected-${qIndex}`}
                        className="mt-1 text-xs text-red-600"
                      >
                        Image rejected for cultural appropriateness. Upload a revised image before
                        this question can be approved.
                      </p>
                    )}
                    {q.illustrativeImageUrl && !q.culturalReviewStatus && (
                      <p
                        role="alert"
                        data-testid={`cultural-review-pending-${qIndex}`}
                        className="mt-1 text-xs text-amber-700"
                      >
                        Cultural-appropriateness review is required before this image-bearing
                        question can be approved.
                      </p>
                    )}
                  </div>

                  {/* RD-affirmed accessibility gate (clinical-lead approval gate — AC2) */}
                  {q.illustrativeImageUrl && (
                    <div
                      className="rounded-md border border-teal-200 bg-teal-50 p-3"
                      data-testid={`accessibility-gate-${qIndex}`}
                    >
                      <p className="mb-2 text-xs font-semibold text-teal-800">
                        Clinical-lead accessibility affirmation (required before approval)
                      </p>
                      <p className="mb-2 text-xs text-teal-700">
                        One-pass test: cover the image, read only the question stem and alt text
                        above, then try to answer. Can the question be answered without seeing the
                        image?
                      </p>
                      <label
                        data-testid={`answerable-from-text-label-${qIndex}`}
                        className="flex cursor-pointer items-center gap-2 text-sm font-medium text-teal-900"
                      >
                        <input
                          type="checkbox"
                          data-testid={`answerable-from-text-checkbox-${qIndex}`}
                          checked={q.answerableFromTextAlone === true}
                          onChange={(e) => handleAnswerableFromTextChange(qIndex, e.target.checked)}
                          className="rounded border-teal-300"
                          aria-label="Answerable from text alone: yes"
                        />
                        Answerable from text alone: yes
                      </label>
                      {q.answerableFromTextAlone !== true && (
                        <p
                          role="alert"
                          data-testid={`accessibility-gate-error-${qIndex}`}
                          className="mt-2 text-xs text-red-600"
                        >
                          Publish is blocked until this is affirmed. If the image is the only way to
                          answer, the question must be revised or the image removed.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Image preview (when URL provided) */}
                  {q.illustrativeImageUrl && (
                    <div>
                      <p className="text-xs font-medium text-gray-600">Preview</p>
                      <img
                        src={q.illustrativeImageUrl}
                        alt={q.illustrativeImageAltText ?? ''}
                        data-testid={`image-preview-${qIndex}`}
                        className="mt-1 max-h-48 rounded-md border border-gray-200 object-contain"
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Add question */}
          <button
            type="button"
            data-testid="add-question-btn"
            onClick={handleAddQuestion}
            disabled={questions.length >= 3}
            className="text-sm font-medium text-teal-600 hover:text-teal-800 disabled:cursor-not-allowed disabled:opacity-40"
          >
            + Add question
          </button>

          {/* Inline scenario validation summary */}
          {allScenarioErrors.length > 0 && (
            <div
              data-testid="scenario-validation-summary"
              className="rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-800"
            >
              <p className="font-medium">
                Scenario field validation errors — resolve before saving:
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                {allScenarioErrors.map(({ qIndex, error }, i) => (
                  <li key={i}>
                    Question {qIndex + 1}: {error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Scope fence violations */}
          {scopeFenceViolations.length > 0 && (
            <div
              data-testid="scope-fence-violations"
              className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-800"
            >
              <p className="font-medium">Scope fence violations — resolve before approving:</p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                {scopeFenceViolations.map((v, i) => (
                  <li key={i}>
                    <span className="font-mono text-xs">
                      [{v.category}] {v.field} — question {v.questionId}: "{v.text}"
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Action row */}
          <div className="flex flex-wrap gap-3 pt-2">
            <button
              type="button"
              data-testid="ask-ai-draft-btn"
              onClick={onRequestAiDraft}
              className="rounded-md border border-teal-300 px-4 py-2 text-sm font-medium text-teal-700 hover:bg-teal-50"
            >
              Ask AI to draft
            </button>

            <button
              type="button"
              data-testid="save-check-btn"
              onClick={handleSave}
              disabled={allScenarioErrors.length > 0}
              className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Save Check
            </button>

            {showApproveBtn && (
              <button
                type="button"
                data-testid="approve-btn"
                onClick={() => {
                  if (initialCheck) onApproveCheck(initialCheck.checkId);
                }}
                disabled={!canApprove}
                className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Approve
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
